import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ExternalLink } from "lucide-react";

const SHEET_API_URL =
  "https://script.google.com/macros/s/AKfycbxnXAJfAUGK2RauSc7gUZ_fMSoOoZCZe5pHTkVi3qCPTwzQFEUzkvjwz8spKbDDU1OL/exec";

const fixCloudinaryUrl = (url) => {
  if (!url) return null;
  if (url.includes("res.cloudinary.com")) {
    let cleanUrl = url.split("?")[0];
    if (!cleanUrl.includes("/f_auto") && !cleanUrl.includes("/video/upload/")) {
      cleanUrl = cleanUrl.replace("/upload/", "/upload/f_auto,q_auto/");
    }
    return cleanUrl;
  }
  return url;
};

// const CATEGORIES = ["Ui / Ux", "Graphic Design", "Branding", "Product Design", "Reels"];

const CATEGORY_MAP = {
  "Ui / Ux": "Website Design",
  "Graphic Design": "Social Media",
  "Branding": "Logo and Branding",
  "Product Design": "Packaging design",
  "Reels": "Reels",
};

/* ─── Lazy Image ─────────────────────────────────────────────── */
const LazyImage = ({ src, alt, className, style, onLoad }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      {
        rootMargin: "50px", // load only near viewport
        threshold: 0.1,
      }
    );

    if (ref.current) observer.observe(ref.current);

    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        ...style,
      }}
    >
      {!loaded && (
        <div
          className="rw-skeleton"
          style={{
            position: "absolute",
            inset: 0,
            borderRadius: "inherit",
          }}
        />
      )}

      {visible && (
        <img
          src={src}
          alt={alt}
          className={className}
          loading="lazy" // browser-level lazy loading
          decoding="async"
          style={{
            opacity: loaded ? 1 : 0,
            transition: "opacity 0.4s ease",
          }}
          onLoad={() => {
            setLoaded(true);
            onLoad && onLoad();
          }}
          onError={() => setLoaded(true)}
        />
      )}
    </div>
  );
};

/* ─── Pinterest Masonry Card ─────────────────────────────────── */
const MasonryCard = ({ work, onClick }) => (
  <motion.div
    layout
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: 20 }}
    transition={{ type: "spring", stiffness: 120, damping: 18 }}
    className="masonry-card"
    onClick={onClick}
  >
    <LazyImage
      src={work.url}
      alt={work.title}
      className="masonry-img"
    />
    <div className="masonry-overlay">
      <div className="masonry-title">{work.title}</div>
      <div className="masonry-cat">{work.category}</div>
      {/* <div className="masonry-views"><Eye size={11} /> {work.views}</div> */}
    </div>
  </motion.div>
);

/* ─── UI/UX Long-scroll Behance-style Card ───────────────────── */
const UiUxCard = ({ work, onClick }) => (
  <motion.div
    layout
    initial={{ opacity: 0, y: 24 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0 }}
    transition={{ type: "spring", stiffness: 100, damping: 20 }}
    className="uiux-card"
    onClick={onClick}
    style={{ cursor: 'pointer' }}
  >
    <div className="uiux-img-wrap">
      <LazyImage src={work.url} alt={work.title} className="uiux-img" />
      <div className="uiux-hover-bar">
        <span className="uiux-hover-title">{work.title}</span>
        <ExternalLink size={14} color="#fff" />
      </div>
    </div>
    <div className="uiux-meta">
      <div>
        <div className="uiux-meta-title">{work.title}</div>
        <div className="uiux-meta-cat">{work.category}</div>
      </div>
      {/* <div className="uiux-meta-views"><Eye size={12} /> {work.views}</div> */}
    </div>
  </motion.div>
);

/* ─── Skeleton ───────────────────────────────────────────────── */
const Skeletons = ({ count, type }) =>
  Array.from({ length: count }).map((_, i) =>
    type === "uiux"
      ? <div key={i} className="uiux-card"><div className="rw-skeleton" style={{ height: 420, borderRadius: 16 }} /></div>
      : <div key={i} className="rw-skeleton masonry-skel" style={{ height: [220, 280, 180, 300, 240, 200][i % 6] }} />
  );

/* ─── Main Component ─────────────────────────────────────────── */
const PortfolioSection = () => {
  const [allWorks, setAllWorks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("Branding");
  const [selectedWork, setSelectedWork] = useState(null);

  // const handleViewWork = useCallback((work) => {
  //   if (!work.viewedLocally) {
  //     const currentViews = Number(work.views);
  //     const newViews = currentViews + 1;

  //     // Update locally
  //     setAllWorks(prev => prev.map(w => w.id === work.id ? { ...w, views: newViews, viewedLocally: true } : w));
      
  //     // Update modal state
  //     setSelectedWork({ ...work, views: newViews, viewedLocally: true });

  //     // Send to counterapi.dev
  //     const safeId = work.id.replace(/[^a-zA-Z0-9_-]/g, '_');
  //     fetch(`https://api.counterapi.dev/v1/sintx-portfolio/${safeId}/up`)
  //       .catch(console.error);
  //   } else {
  //     setSelectedWork(work);
  //   }
  // }, []);

  
const fetchWorks = useCallback(async (isBackground = false) => {
  if (!isBackground) {
    setLoading(true);
    setError(null);
  }

  try {
    const res = await fetch(SHEET_API_URL, {
      cache: "no-store",
      headers: {
        Accept: "application/json",
      },
    });

    const text = await res.text();

    if (!res.ok) {
      throw new Error(`API request failed: HTTP ${res.status}`);
    }

    let json;

    try {
      json = JSON.parse(text);
    } catch {
      console.error(
        "Google Sheets returned non-JSON data:",
        text.slice(0, 500)
      );

      throw new Error(
        "Portfolio API returned HTML instead of JSON. Check Apps Script deployment and permissions."
      );
    }

    if (!json.success || !Array.isArray(json.data)) {
      throw new Error("Invalid portfolio API response");
    }

    const mapped = json.data
      .filter(
        (r) =>
          r.id &&
          r.url &&
          r.deleted !== "TRUE" &&
          r.deleted !== true
      )
      .map((r) => {
        let thumb = fixCloudinaryUrl(r.url);
        let mainUrl = thumb;

        if (r.url.includes("|||")) {
          const parts = r.url.split("|||");
          thumb = fixCloudinaryUrl(parts[0]);
          mainUrl = fixCloudinaryUrl(parts[1]) || thumb;
        }

        return {
          id: r.id,
          url: thumb,
          mainUrl,
          originalUrl: r.url,
          title: r.title || "Untitled",
          category: r.category || "Ui / Ux",
          createdAt: r.uploadedAt
            ? new Date(r.uploadedAt).getTime()
            : Date.now(),
        };
      })
      .sort((a, b) => b.createdAt - a.createdAt);

    setAllWorks(mapped);
    setError(null);

    // Keep a local backup for temporary API failures.
    try {
      localStorage.setItem(
        "portfolio_cache",
        JSON.stringify(mapped)
      );
    } catch (storageError) {
      console.warn("Portfolio cache could not be saved:", storageError);
    }
  } catch (err) {
    console.error("Portfolio loading failed:", err);

    // Preserve already-loaded data if a background refresh fails.
    setAllWorks((current) => {
      if (current.length > 0) return current;

      // Restore cached data if the first request fails.
      try {
        const cached = localStorage.getItem("portfolio_cache");

        if (cached) {
          const parsed = JSON.parse(cached);
          if (Array.isArray(parsed)) return parsed;
        }
      } catch (cacheError) {
        console.warn("Portfolio cache could not be restored:", cacheError);
      }

      return current;
    });

    if (!isBackground) {
      setError(err.message);
    }
  } finally {
    if (!isBackground) {
      setLoading(false);
    }
  }
}, []);


  useEffect(() => { 
    fetchWorks(false); 
    const interval = setInterval(() => {
      fetchWorks(true);
    }, 5 * 60 * 1000); // Poll every 5 minutes
    return () => clearInterval(interval);
  }, [fetchWorks]);

  const filtered = allWorks.filter(w =>
    w.category?.toLowerCase().replace(/\s+/g, "") === activeTab.toLowerCase().replace(/\s+/g, "")
  );

  const isUiUx =
  activeTab === "Ui / Ux" ||
  activeTab === "Product Design" ||
  activeTab === "Branding" || activeTab === "Reels";

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap');

        .port-section {
          background: #f7f8fa;
          padding: 72px 24px 80px;
          font-family: 'Poppins', sans-serif;
        }
        .port-inner { max-width: 1560px; margin: 0 auto; }

        /* ── Header ── */
        .port-label {
        font-size:20px;
          font-weight: 600;
          line-height: 28px;
          letter-spacing: 0em;
          text-transform: uppercase;
          background: linear-gradient(135deg, #129ADC, #B55DD0, #E154AA, #F45A39);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          margin-bottom: 10px;
          width: fit-content; 
          margin: 0 auto 10px;
                
       
        
         display: block; text-align: center;
        }
        .port-heading {
          font-size: clamp(1.5rem, 3vw, 2.1rem); font-weight: 700;
          color: #111; text-align: center; margin: 0 0 80px;
        }

        /* ── Tabs ── */
        /* ── Tabs ── */
.port-tabs {
  display: flex; justify-content: center; gap: 8px;
  flex-wrap: wrap; margin-bottom: 80px;
}
.port-tab {
  padding: 8px 22px; border-radius: 100px; font-size: 0.82rem;
  font-weight: 500; cursor: pointer; border: 1.5px solid #e0e0e0;
  background: #fff; color: #555; transition: all 0.22s ease;
  font-family: 'Poppins', sans-serif;
}
.port-tab:hover { border-color: #B55DD0; color: #B55DD0; }

.port-tab.active {
  border-color: transparent;
  background: linear-gradient(#fff, #fff) padding-box,
              linear-gradient(135deg, #129ADC, #B55DD0, #E154AA, #F45A39) border-box;
  color: #B55DD0;
  box-shadow: 0 4px 20px rgba(181, 93, 208, 0.25); /* ✅ shadow added */
}

.port-tab.active span {
  background: linear-gradient(135deg, #129ADC, #B55DD0, #E154AA, #F45A39);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

        /* ── Skeleton shimmer ── */
        .rw-skeleton {
          background: linear-gradient(90deg, #ececec 25%, #e0e0e0 50%, #ececec 75%);
          background-size: 200% 100%; animation: shimmer 1.4s infinite;
          border-radius: 14px;
        }
        @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }

        /* ══════════════════════════════════════════
           UI/UX — Behance long-scroll layout
        ══════════════════════════════════════════ */
        .uiux-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 24px;
        }
        @media(max-width: 768px){ .uiux-grid{ grid-template-columns: 1fr 1fr; } }
        @media(max-width: 480px){ .uiux-grid{ grid-template-columns: 1fr; } }

        .uiux-card { display: flex; flex-direction: column; gap: 10px; }

        .uiux-img-wrap {
          position: relative; border-radius: 16px; overflow: hidden;
          background: #e8e8e8;
          /* tall aspect for long UI mockup shots */
          aspect-ratio: 4/3;
        }
        .uiux-img {
          width: 100%; height: 100%; object-fit: cover; display: block;
          transition: transform 0.45s ease;
        }
        .uiux-img-wrap:hover .uiux-img { transform: scale(1.03); }

        .uiux-hover-bar {
          position: absolute; bottom: 0; left: 0; right: 0;
          background: linear-gradient(to top, rgba(0,0,0,0.7), transparent);
          padding: 20px 16px 14px;
          display: flex; align-items: center; justify-content: space-between;
          opacity: 0; transition: opacity 0.28s ease;
        }
        .uiux-img-wrap:hover .uiux-hover-bar { opacity: 1; }
        .uiux-hover-title { font-size: 0.8rem; font-weight: 600; color: #fff; }

        .uiux-meta {
          display: flex; align-items: flex-start;
          justify-content: space-between; padding: 0 2px;
        }
        .uiux-meta-title { font-size: 0.82rem; font-weight: 600; color: #111; }
        .uiux-meta-cat {
          font-size: 0.7rem; font-weight: 500; color: #B55DD0;
          text-transform: uppercase; letter-spacing: 0.1em; margin-top: 2px;
        }
        // .uiux-meta-views {
        //   display: flex; align-items: center; gap: 4px;
        //   font-size: 0.72rem; color: #999; white-space: nowrap; margin-top: 2px;
        // }

        /* ══════════════════════════════════════════
           Non-UI/UX — Pinterest masonry
        ══════════════════════════════════════════ */
        .masonry-grid {
          column-count: 7; column-gap: 24px;
        }
        @media(max-width: 768px){ .masonry-grid{ columns: 2; } }
        @media(max-width: 480px){ .masonry-grid{ columns: 2; } }

        

        .masonry-card {
          break-inside: avoid;
          margin-bottom: 20px;
          border-radius: 16px;
          overflow: hidden;
          position: relative;
          cursor: pointer;
          background: #e8e8e8;
        }
          
        .masonry-img {
          width: 100%; display: block;
          transition: transform 0.4s ease;
        }
        .masonry-card:hover .masonry-img { transform: scale(1.04); }

        .masonry-overlay {
          position: absolute; inset: 0;
          background: linear-gradient(to top, rgba(0,0,0,0.68) 0%, transparent 55%);
          opacity: 0; transition: opacity 0.3s ease;
          display: flex; flex-direction: column;
          justify-content: flex-end; padding: 16px;
        }
        .masonry-card:hover .masonry-overlay { opacity: 1; }
        .masonry-title { font-size: 0.82rem; font-weight: 600; color: #fff; margin-bottom: 2px; }
        .masonry-cat {
          font-size: 0.68rem; font-weight: 500; color: rgba(255,255,255,0.7);
          text-transform: uppercase; letter-spacing: 0.1em;
        }
        // .masonry-views {
        //   display: flex; align-items: center; gap: 4px;
        //   font-size: 0.68rem; color: rgba(255,255,255,0.6); margin-top: 4px;
        // }
        .masonry-skel { width: 100%; break-inside: avoid; margin-bottom: 20px; }

        /* ── Skeleton grid wrappers ── */
        .skel-masonry { columns: 3; column-gap: 20px; }
        @media(max-width: 768px){ .skel-masonry{ columns: 2; } }

        /* ── Buttons ── */
        .port-btns { display: flex; gap: 12px; justify-content: center; flex-wrap: wrap; margin-top: 44px; }
        .port-btn-outline {
          padding: 10px 30px; border-radius: 100px; border: 1.5px solid transparent;
          background: linear-gradient(#f7f8fa,#f7f8fa) padding-box,
                      linear-gradient(135deg,#129ADC,#B55DD0,#E154AA,#F45A39) border-box;
          font-family: 'Poppins',sans-serif; font-size: 0.82rem; font-weight: 600; cursor: pointer;
        }
        .port-btn-outline span {
          background: linear-gradient(135deg,#129ADC,#B55DD0,#E154AA,#F45A39);
          -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
        }
        .port-btn-solid {
          padding: 10px 30px; border-radius: 100px; border: none; background: #111; color: #fff;
          font-family: 'Poppins',sans-serif; font-size: 0.82rem; font-weight: 600; cursor: pointer;
          display: inline-flex; align-items: center; gap: 8px; transition: opacity 0.2s;
        }
        .port-btn-solid:hover { opacity: 0.82; }

        /* ── Error / empty ── */
        .port-error {
          text-align: center; padding: 48px; color: #e57373;
          background: #fff5f5; border-radius: 16px; border: 1px solid #fecaca;
        }
        .port-empty { text-align: center; padding: 60px; color: #bbb; font-size: 0.85rem; }
        /* ── Modal ── */
        .portfolio-modal-overlay {
          position: fixed; top: 0; left: 0; right: 0; bottom: 0;
          background: rgba(0,0,0,0.85); z-index: 9999;
          display: flex; justify-content: center; align-items: center;
          padding: 40px 20px;
        }
        .portfolio-modal-content {
    position: relative;
    width: fit-content;
    max-width: 92%;

    /* Glass Background */
    background: rgba(255, 255, 255, 0.08);

    backdrop-filter: blur(24px) saturate(180%);
    -webkit-backdrop-filter: blur(24px) saturate(180%);

    border-radius: 24px;

    display: flex;
    flex-direction: column;

    max-height: 90vh;
    overflow: hidden;

    border: 1px solid rgba(255, 255, 255, 0.18);

    box-shadow:
        0 8px 32px rgba(0, 0, 0, 0.25),
        inset 0 1px 0 rgba(255, 255, 255, 0.25),
        inset 0 -1px 0 rgba(255, 255, 255, 0.05);

    position: relative;
}

    .portfolio-modal-content::after {
    content: "";
    position: absolute;
    width: 250px;
    height: 250px;

    top: -80px;
    right: -80px;

    border-radius: 50%;

    background: radial-gradient(
        circle,
        rgba(255,255,255,0.18),
        transparent 70%
    );

    pointer-events: none;
    filter: blur(30px);
}
    .portfolio-modal-overlay {
    background: rgba(0, 0, 0, 0.35);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
}
        .portfolio-modal-close {
          position: absolute; top: 16px; right: 16px;
          background: rgba(255,255,255,0.1); border: none; color: #fff;
          width: 36px; height: 36px; border-radius: 50%; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          z-index: 10; transition: all 0.2s; backdrop-filter: blur(4px);
        }
        .portfolio-modal-close:hover { background: rgba(255,255,255,0.2); transform: scale(1.1); }
        .portfolio-modal-header {
          padding: 24px 32px; border-bottom: 1px solid rgba(255,255,255,0.08);
          flex-shrink: 0;
        }
        .portfolio-modal-header h3 {
          margin: 0; color: #fff; font-size: 1.5rem; font-weight: 600;
        }
        .portfolio-modal-header p {
          margin: 6px 0 0 0; font-size: 0.85rem; font-weight: 500;
          color: #fff; text-transform: uppercase; letter-spacing: 1px;
        }
        .portfolio-modal-body {
          flex: 1; overflow-y: auto; overflow-x: hidden;
          background: #0a0a0a; scroll-behavior: smooth;
        }
        .portfolio-modal-body::-webkit-scrollbar { width: 8px; }
        .portfolio-modal-body::-webkit-scrollbar-track { background: #111; }
        .portfolio-modal-body::-webkit-scrollbar-thumb { background: #333; border-radius: 4px; }
        .portfolio-modal-body::-webkit-scrollbar-thumb:hover { background: #555; }
        .portfolio-modal-body img {
          width: 100%; height: auto; display: block;
        }
          .graphic-design-modal {
              width: auto;
          }
          .graphic-design-modal .portfolio-modal-body img {
              width: auto;
              height: auto;
              max-width: 88vw;
              max-height: 80vh;
              display: block;
              margin: 0 auto;
          }

          @media (max-width: 768px) {
          .graphic-design-modal .portfolio-modal-body img {
              width: 98%;
              height: 80%;
              max-width: 92vw;
              max-height: 80%;
              display: block;
              margin: 0 auto;
          }
        }
      `}</style>

      {/* Modal */}
      <AnimatePresence>
        {selectedWork && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedWork(null)}
            className="portfolio-modal-overlay"
          >
            <motion.div
              initial={{ y: 50, opacity: 0, scale: 0.95 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 20, opacity: 0, scale: 0.95 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
              className={`portfolio-modal-content ${
  selectedWork?.category === "Graphic Design"
    ? "graphic-design-modal"
    : ""
}`}
            >
              <button onClick={() => setSelectedWork(null)} className="portfolio-modal-close">
                ✕
              </button>
              <div className="portfolio-modal-header">
                  <h3>{selectedWork.title}</h3>
                  <p>{selectedWork.category}</p>
              </div>
              <div className="portfolio-modal-body">
                 {selectedWork.category.toLowerCase() === "reels" ? (
                   <video 
                     src={selectedWork.mainUrl || selectedWork.url} 
                     controls 
                     autoPlay 
                     style={{ width: "auto", height: "auto", maxHeight: "78vh", display: "block", margin: "0 auto" }}
                   />
                 ) : (
                   <img 
                       src={selectedWork.mainUrl || selectedWork.url} 
                       alt={selectedWork.title} 
                       loading="lazy"
                   />
                 )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <section className="port-section" id="portfolio">
        <div className="port-inner">

          {/* Header */}
          <span className="port-label">Our Recent Works</span>
          <h2 className="port-heading">Made with brains, not just pixels.</h2>

          {/* Category tabs */}
          <div className="port-tabs">
            {Object.entries(CATEGORY_MAP).map(([key, value]) => (
              <button
                key={key}
                className={`port-tab${activeTab === key ? " active" : ""}`}
                onClick={() => setActiveTab(key)}
              >
                <span>{value}</span>
              </button>
            ))}
          </div>

          {/* Content */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.28 }}
            >
              {loading ? (
                isUiUx ? (
                  <div className="uiux-grid"><Skeletons count={6} type="uiux" /></div>
                ) : (
                  <div className="skel-masonry"><Skeletons count={6} type="masonry" /></div>
                )
              ) : error ? (
                <div className="port-error">
                  ⚠ {error} —{" "}
                  <button onClick={fetchWorks} style={{ textDecoration: "underline", cursor: "pointer", background: "none", border: "none", color: "inherit" }}>
                    Retry
                  </button>
                </div>
              ) : filtered.length === 0 ? (
                <div className="port-empty">✨ No projects in this category yet.</div>
              ) : isUiUx ? (
                /* ── UI/UX: tall single-image Behance layout ── */
                <div className="uiux-grid">
                  {filtered.map(work => <UiUxCard key={work.id} work={work} onClick={() => setSelectedWork(work)} />)}
                </div>
              ) : (
                /* ── Others: Pinterest masonry ── */
                <div className="masonry-grid">
                  {/* {filtered.map(work => <MasonryCard key={work.id} work={work} />)} */}
                  {filtered.map(work => <MasonryCard key={work.id} work={work} onClick={() => setSelectedWork(work)} />)}  
                  {/* the up cod is for popup of grafic design and branding work, if you want to popup ui/ux work then use the below code and comment the above one */}
                </div>
              )}
            </motion.div>
          </AnimatePresence>

          {/* Buttons */}
          {/* <div className="port-btns">
            <motion.button
              className="port-btn-outline"
              whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}
              onClick={() => window.location.href = "/our-work"}
            >
              <span>See all</span>
            </motion.button>
            <motion.button
              className="port-btn-solid"
              whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}
              onClick={() => window.location.href = "/contact"}
            >
              Work With Me <ExternalLink size={14} />
            </motion.button>
          </div> */}

        </div>
      </section>
    </>
  );
};

export default PortfolioSection;